import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import useSettings from '../../utils/useSettings';
import API from '../../utils/api';
import { useTranslation } from 'react-i18next';
import { canGenerateStories, entitledPlan } from '../../utils/entitlements';
import AppStoreBadges from '../AppStoreBadges';
import { isNative } from '../../utils/capacitor';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { darkMode, toggleDarkMode } = useTheme();
  const [pendingCount, setPendingCount] = useState(0);
  const [affiliatePath, setAffiliatePath] = useState('/affiliate/apply');
  const [moreOpen, setMoreOpen] = useState(false);
  const { settings } = useSettings();
  const flags = settings.featureFlags || {};

  useEffect(() => {
    if (!user) return;
    API.get('/stories/approval/queue')
      .then(res => setPendingCount(res.data.count || 0))
      .catch(() => {});
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user) return;
    API.get('/affiliate/me')
      .then(() => setAffiliatePath('/affiliate/dashboard'))
      .catch(() => setAffiliatePath('/affiliate/apply'));
  }, [user]);

  // Primary nav — 5 items max (Hick's Law: fewer choices = faster decisions)
  const primaryNav = [
    { path: '/dashboard',          icon: 'bi-house-heart-fill', label: 'Home',          tour: null },
    // read_only co-parents get a 403 from generateStory, so don't offer it.
    canGenerateStories(user) && { path: '/dashboard/generate', icon: 'bi-magic', label: 'Create Story', tour: 'nav-generate', featured: true },
    { path: '/dashboard/library',  icon: 'bi-book-half',        label: 'My Library',    tour: 'nav-library' },
    { path: '/dashboard/children', icon: 'bi-people-fill',      label: 'Children',      tour: 'nav-children' },
    { path: '/dashboard/settings', icon: 'bi-gear-fill',        label: 'Settings',      tour: 'nav-settings' },
  ].filter(Boolean);

  // Secondary nav — revealed on demand (progressive disclosure)
  const secondaryNav = [
    { path: '/dashboard/reports',        icon: 'bi-bar-chart-fill',        label: t('nav.reports') },
    flags.storySeries && { path: '/dashboard/series',   icon: 'bi-collection-play-fill', label: t('nav.series') },
    { path: '/dashboard/characters',     icon: 'bi-stars',                 label: 'Characters' },
    flags.community && { path: '/community',            icon: 'bi-globe2',               label: t('nav.community') },
    { path: '/dashboard/approval-queue', icon: 'bi-eye-fill',              label: t('nav.approvalQueue'), badge: pendingCount },
    { path: affiliatePath,               icon: 'bi-megaphone-fill',        label: t('nav.affiliate') },
  ].filter(Boolean);

  const isActive = (path) =>
    location.pathname === path ||
    (path === '/dashboard/reports' && location.pathname.includes('/report'));

  // Auto-open More if the current page is in the secondary nav
  useEffect(() => {
    const inMore = secondaryNav.some(item => item.path && isActive(item.path));
    if (inMore) setMoreOpen(true);
  }, [location.pathname]); // eslint-disable-line

  const planBadgeStyle = {
    free:      { background: '#6c757d', color: '#fff' },
    basic:     { background: '#6f42c1', color: '#fff' },
    family:    { background: '#fd7e14', color: '#fff' },
  };
  // The plan in force (lapsed, expired-gift and co-parent cases resolved), not
  // the raw field — which badged a lapsed subscriber FAMILY and a co-parent FREE.
  const shownPlan  = entitledPlan(user);
  const badgeStyle = planBadgeStyle[shownPlan] || planBadgeStyle.free;

  const handleLogout = () => { logout(); navigate('/'); };

  const NavLink = ({ item }) => (
    <Link
      key={item.path}
      to={item.path}
      data-tour={item.tour || undefined}
      className={`nav-link d-flex align-items-center gap-2 px-3 py-2 mb-1 rounded-3 ${isActive(item.path) ? 'active' : ''}`}
      style={item.featured && !isActive(item.path) ? {
        background: 'rgba(255,193,7,0.15)',
        border: '1px solid rgba(255,193,7,0.3)',
        color: '#ffc107',
      } : {}}
    >
      <i className={`bi ${item.icon}`}></i>
      <span className="flex-grow-1">{item.label}</span>
      {item.badge > 0 && (
        <span className="badge rounded-pill ms-auto"
          style={{ background: '#2563eb', color: '#fff', fontSize: '10px', minWidth: '18px' }}>
          {item.badge}
        </span>
      )}
    </Link>
  );

  return (
    <div className="sidebar d-flex flex-column p-3" style={{ width: '240px', height: '100vh', overflowY: 'auto' }}>

      {/* Brand */}
      <Link to="/dashboard" className="text-decoration-none mb-4 mt-2 d-block text-center">
        <span style={{ fontFamily: "'Fredoka One',cursive", color: '#fff', fontSize: '22px', letterSpacing: '0.5px' }}>
          🌙 Dreamy Bedtime
        </span>
      </Link>

      {/* Primary navigation */}
      <nav className="flex-grow-1" style={{ overflowY: 'auto', minHeight: 0 }}>
        {primaryNav.map(item => <NavLink key={item.path} item={item} />)}

        {/* More section — progressive disclosure */}
        <div style={{ margin: '8px 0 4px' }}>
          <button
            onClick={() => setMoreOpen(o => !o)}
            className="nav-link d-flex align-items-center gap-2 px-3 py-2 mb-1 w-100 border-0 rounded-3"
            style={{ background: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.55)', textAlign: 'left', fontSize: '13px' }}>
            <i className={`bi ${moreOpen ? 'bi-chevron-down' : 'bi-chevron-right'}`} style={{ fontSize: '11px' }}></i>
            <span>More features</span>
          </button>

          {moreOpen && (
            <div style={{ paddingLeft: '8px' }}>
              {secondaryNav.map(item => <NavLink key={item.path} item={item} />)}
            </div>
          )}
        </div>
      </nav>

      {/* Bottom actions — compact */}
      <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
        <Link to="/gift" className="nav-link d-flex align-items-center gap-2 px-3 py-2 mb-1 rounded-3"
          style={{ color: '#f9c74f' }}>
          <i className="bi bi-gift-fill"></i>
          <span>{t('nav.giftStory')}</span>
        </Link>

        {/* entitledPlan: a lapsed Family subscriber keeps plan==='family', which
            hid this upgrade prompt from precisely the churned customer it exists
            to win back. */}
        {entitledPlan(user) !== 'family' && (
          <Link to="/pricing" className="nav-link d-flex align-items-center gap-2 px-3 py-2 mb-1 rounded-3"
            style={{ color: '#f9c74f' }}>
            <i className="bi bi-star-fill"></i>
            <span>{entitledPlan(user) === 'basic' ? t('nav.upgradeToFamily') : t('nav.upgradePlan')}</span>
          </Link>
        )}

        <Link to="/guide" className="nav-link d-flex align-items-center gap-2 px-3 py-2 mb-1 rounded-3"
          style={{ color: '#c9a8ff' }}>
          <i className="bi bi-question-circle-fill"></i>
          <span>{t('nav.help')}</span>
        </Link>

        <button
          onClick={toggleDarkMode}
          className="nav-link d-flex align-items-center gap-2 px-3 py-2 mb-2 w-100 border-0 rounded-3"
          style={{ background: 'none', cursor: 'pointer', color: darkMode ? '#f9c74f' : '#c9a8ff', textAlign: 'left' }}>
          <i className={`bi ${darkMode ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`}></i>
          <span>{darkMode ? 'Light Mode' : 'Night Mode'}</span>
        </button>

        {/* Get the app — website only (AppStoreBadges renders nothing in the apps) */}
        {!isNative() && (
          <div className="px-2 mb-2">
            <div style={{ color: '#c9a8ff', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
              <i className="bi bi-phone me-1"></i>Get the app
            </div>
            <AppStoreBadges placement="sidebar" height={30} />
          </div>
        )}

        {/* User card */}
        <div className="d-flex align-items-center gap-2 px-2 py-2 rounded-3"
          style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
            style={{ width: '36px', height: '36px', background: '#6f42c1', fontSize: '14px', flexShrink: 0 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-grow-1 overflow-hidden">
            <div className="fw-semibold text-truncate" style={{ fontSize: '13px', color: '#fff' }}>{user?.name}</div>
            <span className="badge rounded-pill" style={{ ...badgeStyle, fontSize: '10px' }}>
              {shownPlan.toUpperCase()}
            </span>
          </div>
          <button className="btn btn-link p-0" onClick={handleLogout} title={t('nav.signOut')}
            style={{ color: 'rgba(255,255,255,0.5)' }}>
            <i className="bi bi-box-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
